import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, View } from 'react-native';
import { useLazyQuery, gql } from '@apollo/client';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faEdit } from '@fortawesome/free-solid-svg-icons';

import NewFee from 'app/src/components/Accounting/Fees/new';
import EditFee from 'app/src/components/Accounting/Fees/edit';
import { Container, Input, Results } from 'app/src/components/Autocomplete';
import Modal from 'app/src/elements/Modal';
import { Text } from 'app/src/styles';
import { currency } from 'app/src/utils/stringHelpers';

import { Field, FieldLabel, errorsFor, PickerInput } from './inputs';

const SEARCH_ASSET_FIELDS = gql`
  fragment SearchAssetFields on Asset {
    id
    stockNumber
    lastSix
    name
    seller {
      id
      name
      feeMarkups {
        feeType
        markupType
        amount
        defaultMarkup
      }
    }
    saleAssets {
      id
      lotNumber
      sale { name }
    }
    fees {
      id
      type
      amount
      buyerFee
      saleAsset { id }
    }
  }
`;

const SEARCH_ASSETS = gql`
  ${SEARCH_ASSET_FIELDS}
  query NoUserLogAssetSearch(
    $term: String!
  ) {
    search(
      type: "Asset"
      term: $term
    ) {
      ... on Asset {
         ...SearchAssetFields
      }
    }
  }
`;

const ASSET = gql`
  ${SEARCH_ASSET_FIELDS}
  query NoUserLogAsset($id: ID!) {
    asset(id: $id) {
      ...SearchAssetFields
    }
  }
`;

const ResultContainer = styled(Pressable)`
  flex-direction: row;
  column-gap: 10px;
  margin: 5px;
`;

const Name = styled(View)`
  flex-direction: row;
  gap: 10px;
`;

const NameText = styled(Text)`
  font-weight: 600;
`;

const Result = ({ result, handleSelect }) => {
  return (
    <ResultContainer onPress={() => handleSelect(result)}>
      <Name>
        <NameText>{result.stockNumber || result.lastSix}</NameText>
        <Text>{result.seller.name}</Text>
        <Text>{result.name}</Text>
      </Name>
    </ResultContainer>
  );
};

const AssetAutocomplete = props => {
  const {
    field, asset, setAsset, setValue, namePrefix, createFee,
    getAsset, loading, term, setTerm,
  } = props;
  const [searching, setSearching] = useState(false);
  const [blurTimeout, setBlurTimeout] = useState();
  const [hasBlurred, setHasBlurred] = useState(false)
  const [offsets, setOffsets] = useState({ top: 0, left: 0 });
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!asset && field.value && !loading) {
      getAsset({ variables: { id: field.value } });
    }
  }, [asset, field.value]);

  const [searchAssets] = useLazyQuery(SEARCH_ASSETS, {
    onCompleted: ({ search }) => setResults(search)
  });

  const ref = useCallback(element => {
    if (element) {
      setOffsets(element.getBoundingClientRect());
    }
  }, []);

  const handleChange = value => {
    if (blurTimeout) {
      window.clearTimeout(blurTimeout);
      setBlurTimeout(null);
    }

    setTerm(value);
    if (!searching) { setSearching(true); }

    if (value.length > 0) {
      searchAssets({ variables: { term: value } });
    }

    if (asset) {
      field.onChange(null);
      setAsset(null);
      setValue(`${namePrefix}.saleAssetId`, '');
      setValue(`${namePrefix}.feeId`, '');
    }
  };

  const handleKeyPress = e => {
    if ('Enter' === e.nativeEvent.key) {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (hasBlurred) {
      if (!field.value) { setTerm(''); }
      setSearching(false);
      setHasBlurred(false);
    }
  }, [hasBlurred, field]);

  const handleBlur = () => {
    setBlurTimeout(window.setTimeout(() => setHasBlurred(true), 200));
    field.onBlur();
  };

  const handleSelect = result => {
    field.onChange(result.id);
    setTerm(result.stockNumber || result.lastSix);
    setAsset(result);
    setSearching(false);
  };

  const styles = {
    maxWidth: 500,
    top: offsets.top + 12,
    left: offsets.left,
  };

  return (
    <Container style={{ marginBottom: 0 }}>
      <div ref={ref} />
      <Input
        value={term}
        style={{ marginLeft: 0 }}
        onChangeText={handleChange}
        onKeyPress={handleKeyPress}
        onBlur={handleBlur}
      />
      {searching && term.length > 0 && results.length > 0 && (
        createPortal(
          <Results style={styles}>
            {results.map(result => (
              <Result
                key={`${result.__typename}-${result.id}`}
                result={result}
                handleSelect={handleSelect}
              />
            ))}
          </Results>, document.body
        )
      )}
    </Container>
  );
};

const AssetInput = props => {
  const {
    name, label, hint, control, rules, errors, inline, setValue,
    extraLabels, width, txn, onFeeCreated
  } = props;

  const namePrefix = name.split('.').slice(0,-1).join('.');

  const [asset, setAsset] = useState();
  const [saleAssetId, setSaleAssetId] = useState();
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [assetTerm, setAssetTerm] = useState('');
  const { watch } = useFormContext();

  const [getAsset, { loading }] = useLazyQuery(ASSET, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      setAsset(data.asset);
      setAssetTerm(data.asset.stockNumber || data.asset.lastSix);
    }
  });

  const expenseAmount = watch(`${namePrefix}.debitLessCredit`);
  const feeId = watch(`${namePrefix}.feeId`);

  const saleAssetOptions = asset ?
    asset.saleAssets.map(saleAsset => ({
      value: saleAsset.id,
      label: `${saleAsset.lotNumber} ${saleAsset.sale.name}`
    })) : [];

  const feeOptions = asset ?
    asset.fees.filter(fee => (
      !(fee.saleAsset?.id) || !saleAssetId || fee.saleAsset.id === saleAssetId
    )).map(fee => ({
      value: fee.id,
      label: `${fee.type} ${currency(fee.amount)}`
    })) : [];

  return (
    <>
      <Field inline={inline} errors={errorsFor(errors, name)} width={width}>
        {!inline && (
          <FieldLabel
            name={name}
            label={label}
            hint={hint}
            errors={errors}
          />
        )}
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <AssetAutocomplete
              field={field}
              asset={asset}
              setAsset={setAsset}
              setValue={setValue}
              namePrefix={namePrefix}
              getAsset={getAsset}
              loading={loading}
              term={assetTerm}
              setTerm={setAssetTerm}
            />
          )}
        />
      </Field>

      {extraLabels.saleAssetId && (
        <PickerInput
          label={extraLabels.saleAssetId}
          name={`${namePrefix}.saleAssetId`}
          options={saleAssetOptions}
          control={control}
          errors={errors}
          inline={inline}
          handleChange={setSaleAssetId}
          width={width}
        />
      )}

      {extraLabels.feeId && (
        <PickerInput
          label={extraLabels.feeId}
          name={`${namePrefix}.feeId`}
          options={feeOptions}
          control={control}
          errors={errors}
          inline={inline}
          width={width - 30}
        />
      )}

      {extraLabels.feeId && (
        asset ? (
          feeId ? (
            <Pressable
              onPress={() => setShowEditModal(true)}
              style={{ width: 30, alignSelf: 'center' }}
            >
              <FontAwesomeIcon icon={faEdit} title="Edit Fee" />
            </Pressable>
          ) : (
            <Pressable
              onPress={() => setShowNewModal(true)}
              style={{ width: 30, alignSelf: 'center' }}
            >
              <FontAwesomeIcon icon={faCirclePlus} title="Create Fee" />
            </Pressable>
          )
        ) : (
          <View style={{ width: 30 }}></View>
        )
      )}

      {showNewModal && (
        <Modal
          showModal={showNewModal}
          setShowModal={setShowNewModal}
          modalType="Add Fee"
        >
          <NewFee
            asset={asset}
            saleAssetId={saleAssetId}
            expenseAmount={expenseAmount}
            txn={txn}
            onCompleted={data => {
              getAsset({ variables: { id: asset.id } });
              if (onFeeCreated) {
                onFeeCreated(data);
              } else {
                setValue(`${namePrefix}.feeId`, data.createFee.fee.id);
              }
              setShowNewModal(false);
            }}
          />
        </Modal>
      )}

      {showEditModal && (
        <Modal
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          modalType="Edit Fee"
        >
          <EditFee
            asset={asset}
            expenseAmount={expenseAmount}
            feeId={feeId}
            onCompleted={() => {
              getAsset({ variables: { id: asset.id } });
              setShowEditModal(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default AssetInput;
